/* eslint-disable eqeqeq */
import { useEffect, useRef } from 'react';
import type { ISizerFilterSize } from 'app/services/sizerApi';

/**
 * Phase 4b — the "Allowable filter sizes" multiselect (settings.tmpl:231-262)
 * and its five handlers (main.js:766-836). Markup reproduced verbatim, ids and
 * all: `#show_allowable_filter_sizes`, `#allowable_filter_sizes_list`,
 * `#apply-sizes`, `.allowable_filter_sizes_output`.
 *
 * The state machine lives in `useSizerSettings` (staging vs committed, the All
 * box, Apply's disabled flag, the `max_different_sizes` clamp); this component
 * owns only the DOM, the outside-click dismissal and `isEllipsisActive`.
 */

interface AllowableFilterSizesProps {
  /** every master filter size (the checkbox list). */
  filterSizes: ISizerFilterSize[];
  /** the COMMITTED set — drives the chip strip. */
  committed: ISizerFilterSize[];
  /** the STAGING set — drives the checkboxes. */
  staged: ISizerFilterSize[];
  open: boolean;
  allChecked: boolean;
  applyDisabled: boolean;
  /** bumped when `isEllipsisActive()` must re-measure (main.js:755, :803). */
  ellipsisNonce: number;
  onToggle: () => void;
  onHide: () => void;
  onChange: (value: string, checked: boolean) => void;
  onApply: () => void;
}

const AllowableFilterSizes = ({
  filterSizes,
  committed,
  staged,
  open,
  allChecked,
  applyDisabled,
  ellipsisNonce,
  onToggle,
  onHide,
  onChange,
  onApply,
}: AllowableFilterSizesProps) => {
  const outputRef = useRef<HTMLSpanElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  /**
   * `isEllipsisActive` (main.js:823-836) verbatim, INCLUDING the latent bug at
   * :835: the removal branch removes `data-status`, NOT `data-tooltip`, so once
   * the chip strip has overflowed the `data-tooltip` attribute is never cleaned
   * up. Reproduced (PARITY-ONLY), hence the direct DOM writes rather than JSX
   * attributes — the asymmetry cannot be expressed declaratively.
   *
   * Legacy calls this at exactly two moments: 100 ms after the gear opens the
   * settings popup (:752-758) and at the end of `applySizes` (:803) — never on
   * a plain render, which is why it is nonce-driven here rather than an effect
   * on `committed`.
   */
  useEffect(() => {
    if (ellipsisNonce === 0) return;
    const element = outputRef.current;
    if (!element) return;
    const isActive = element.scrollWidth > element.clientWidth;
    if (isActive) {
      let tooltip = '';
      committed.forEach((el, idx) => {
        tooltip += `${el.name}${idx !== committed.length - 1 ? ', ' : ''}`;
      });
      element.classList.add('show-tooltip');
      element.setAttribute('data-tooltip', tooltip);
      return;
    }
    element.classList.remove('show-tooltip');
    element.removeAttribute('data-status');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ellipsisNonce]);

  /**
   * The dismiss half of the blanket body click handler (main.js:168-171): any
   * click that is neither on `#show_allowable_filter_sizes` nor inside
   * `#allowable_filter_sizes_list` closes an open list. Scoped to this
   * component rather than a global hook because the list is the only thing it
   * touches; 4d's port of the SAME handler must therefore implement only its
   * OTHER half (the `.popup-wrapper` backdrop branch at :165-166).
   */
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (buttonRef.current?.contains(target)) return;
      if (listRef.current?.contains(target)) return;
      onHide();
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [open, onHide]);

  const stagedIds = new Set(staged.map((el) => el.id));

  return (
    <div className="multi-select-group">
      <button
        id="show_allowable_filter_sizes"
        type="button"
        className="multi-select-group-button icon-mounting icon-selection"
        tabIndex={-1}
        data-target=".allowable_filter_sizes_list"
        ref={buttonRef}
        onClick={onToggle}
      />
      <div
        className={`allowable_filter_sizes_list multi-select-list${open ? ' active' : ''}`}
        id="allowable_filter_sizes_list"
        ref={listRef}
      >
        <ul>
          <li>
            <label>
              <input
                type="checkbox"
                value=""
                className="allowable_filter_sizes"
                name="allowable_filter_sizes"
                tabIndex={-1}
                checked={allChecked}
                onChange={(e) => onChange('', e.target.checked)}
              />
              <span className="label">All</span>
            </label>
          </li>
          {filterSizes.map((size) => (
            <li key={size.id}>
              <label>
                <input
                  type="checkbox"
                  value={size.id}
                  className="allowable_filter_sizes"
                  name="allowable_filter_sizes"
                  tabIndex={-1}
                  checked={stagedIds.has(size.id)}
                  onChange={(e) => onChange(String(size.id), e.target.checked)}
                />
                <span className="label">{size.name}</span>
              </label>
            </li>
          ))}
          <li className="apply-btn-li">
            <button
              type="button"
              className={`btn${applyDisabled ? ' non-clickable' : ''}`}
              id="apply-sizes"
              onClick={() => {
                // `.non-clickable` is CSS-only in legacy (pointer-events), so
                // guard here to keep the same inertness.
                if (!applyDisabled) onApply();
              }}
            >
              Apply
            </button>
          </li>
        </ul>
      </div>
      <span className="checked-values output allowable_filter_sizes_output" ref={outputRef}>
        {committed.map((el) => (
          <span key={el.id}>{el.name}</span>
        ))}
      </span>
    </div>
  );
};

export default AllowableFilterSizes;
